import { createFileRoute, Link } from "@tanstack/react-router";
import { Home, Calendar, Star, FileSignature, ArrowRight, Key, Users, MapPin } from "lucide-react";
import { FeaturePage } from "@/components/marketing/FeaturePage";
import { alternateLinkList } from "@/lib/i18n/locale";
import hero from "@/assets/nfc-card-lifestyle.jpg";
import booking from "@/assets/phone-card.jpg";
import reviews from "@/assets/ai-abstract.jpg";
import nurture from "@/assets/cards-sharing.webp";

export const Route = createFileRoute("/vi/solutions/real-estate")({
  head: () => ({
    meta: [
      { title: "Thẻ NFC & CRM cho môi giới bất động sản — banana.cards" },
      { name: "description", content: "Chạm để chia sẻ danh sách bất động sản, đặt lịch xem nhà tại chỗ, tự động xin đánh giá, và luôn được nhớ đến bởi mọi khách cũ trong nhiều năm." },
      { property: "og:title", content: "Thẻ NFC & CRM cho môi giới bất động sản — banana.cards" },
      { property: "og:description", content: "Listing, đặt lịch, hợp đồng và đánh giá — trên một chiếc thẻ bạn chỉ cần chạm để chia sẻ." },
      { property: "og:image", content: hero },
      { property: "og:locale", content: "vi_VN" },
    ],
    links: alternateLinkList("/solutions/real-estate", "vi"),
  }),
  component: Page,
});

function Page() {
  return (
    <FeaturePage
      eyebrow="Cho bất động sản"
      title="Luôn được nhớ đến —"
      italic="suốt nhiều năm."
      description="Chạm thẻ tại buổi mở nhà, đặt lịch xem nhà lần hai tại chỗ, gửi hợp đồng chỉ một lần nhấp, và yêu cầu đánh giá 5 sao gửi đi vào ngày sau khi chốt — tự động."
      heroImage={hero}
      metrics={[
        { v: "4.9★", l: "Điểm đánh giá trung bình" },
        { v: "+62%", l: "Listing nhiều hơn từ giới thiệu" },
        { v: "7 năm", l: "Chu kỳ nuôi dưỡng trung bình" },
      ]}
      bullets={[
        { icon: Calendar, title: "Đặt lịch trên thẻ", desc: "Khách chạm, chọn giờ xem nhà và đặt cọc — tất cả không rời hồ sơ có thương hiệu của bạn." },
        { icon: Star, title: "Tự động xin đánh giá", desc: "Sau mỗi giao dịch, Banana gửi đúng yêu cầu đánh giá (Google, Zillow, Realtor) vào đúng thời điểm, bằng giọng văn của bạn." },
        { icon: FileSignature, title: "Hợp đồng & công bố, đã ký", desc: "Thỏa thuận listing, ủy quyền mua, công bố — tất cả ký điện tử trong Banana, lưu trữ và sẵn sàng tuân thủ." },
      ]}
      deepDives={[
        {
          eyebrow: "Chạm đầu tiên",
          title: "Từ mở nhà",
          italic: "đến xem lần hai — trong 30 giây.",
          description: "Chạm thẻ NFC vào điện thoại khách. Họ thấy listing, đánh giá và lịch trực tiếp. Họ đặt lịch xem tiếp theo trước khi rời căn nhà.",
          points: [
            "Carousel listing nổi bật trên hồ sơ",
            "Lịch trực tiếp với khung giờ đặt được",
            "Đặt cọc qua Stripe cho khách nghiêm túc",
            "Tự động thêm vào pipeline nuôi dưỡng",
          ],
          image: booking,
        },
        {
          eyebrow: "Uy tín",
          title: "Trở thành môi giới với",
          italic: "100+ đánh giá 5 sao.",
          description: "Mỗi lần chốt giao dịch khởi động một chuỗi: SMS hôm sau cho Google, email ngày thứ 3 cho Zillow, nhắc nhẹ ngày 14 nếu chưa phản hồi. Bạn thức dậy với đánh giá mới mà không cần nhấc tay.",
          points: [
            "Định tuyến đánh giá đa nền tảng",
            "AI viết lại phản hồi theo giọng văn của bạn",
            "Chặn phản hồi tiêu cực (gửi cho bạn trước)",
            "Widget nhúng cho trang listing",
          ],
          image: reviews,
          reverse: true,
        },
        {
          eyebrow: "Nuôi dưỡng",
          title: "Có mặt trong cuộc đời họ",
          italic: "trong 7 năm tới.",
          description: "Hầu hết khách cũ chuyển nhà 5–7 năm/lần. Banana gửi lời chúc kỷ niệm mua nhà, cập nhật thị trường khu phố, và chúc sinh nhật — để khi họ sẵn sàng bán, bạn là cuộc gọi duy nhất.",
          points: [
            "Tự động hóa kỷ niệm & sinh nhật",
            "Báo cáo thị trường khu phố hàng quý",
            "Chuỗi xin giới thiệu",
            "Gắn nhãn & phân khúc khách cũ",
          ],
          image: nurture,
        },
      ]}
      howItWorks={[
        { title: "Đặt thẻ", desc: "Kim loại hoặc PVC có thương hiệu với logo, ảnh và sàn giao dịch. Giao trong 5–7 ngày." },
        { title: "Kết nối listing & lịch", desc: "Đồng bộ feed MLS, lịch Google/Outlook và tài khoản Stripe cho đặt cọc." },
        { title: "Chạm tại mọi buổi xem nhà", desc: "Thu lead, đặt lịch tiếp theo, kích hoạt pipeline nuôi dưỡng — chỉ một lần chạm." },
        { title: "Chốt & để nó tự chạy", desc: "Hợp đồng ký điện tử trong Banana. Đánh giá kích hoạt tự động. Nuôi dưỡng 7 năm bắt đầu." },
      ]}
      capabilities={[
        "Thẻ NFC có thương hiệu",
        "Carousel listing nổi bật",
        "Đặt lịch & đồng bộ lịch",
        "Thu đặt cọc qua Stripe",
        "Ký điện tử thỏa thuận listing & mua",
        "Tự động đánh giá đa nền tảng",
        "Chuỗi nuôi dưỡng khách cũ",
        "Tích hợp MLS & Zillow",
      ]}
      faq={[
        { q: "Có hoạt động với MLS và Zillow không?", a: "Có. Banana lấy listing hoạt động từ hầu hết feed MLS lớn và từ Zillow Premier Agent — chúng xuất hiện tự động trên hồ sơ và cập nhật theo thời gian thực khi trạng thái đổi (active → pending → sold)." },
        { q: "Khách có thể đặt lịch xem nhà trực tiếp từ thẻ không?", a: "Có. Khi ai đó chạm thẻ, họ thấy lịch trực tiếp (Google, Outlook hoặc iCloud) với các khung giờ bạn đã mở cho xem nhà. Họ chọn một, tùy chọn trả đặt cọc qua Stripe, và cuộc hẹn vào lịch kèm toàn bộ thông tin liên hệ." },
        { q: "Hợp đồng và công bố hoạt động ra sao?", a: "Banana có engine chữ ký điện tử tuân thủ ESIGN, UETA và eIDAS. Dùng template của chúng tôi cho thỏa thuận listing, ủy quyền mua và công bố, hoặc tải lên của sàn bạn. Khách ký trên điện thoại trong dưới một phút. PDF đã ký được lưu trữ kèm audit trail." },
        { q: "Tự động đánh giá thực sự gửi gì?", a: "Sau khi bạn đánh dấu giao dịch hoàn tất, một chuỗi bắt đầu: ngày 1 — SMS cảm ơn kèm link đánh giá Google; ngày 3 — email kèm link Zillow nếu chưa có đánh giá Google; ngày 14 — nhắc nhẹ. AI viết lại mọi tin nhắn bằng giọng văn của bạn. Phản hồi tiêu cực được chặn và gửi cho bạn trước, không bao giờ công khai." },
        { q: "Nuôi dưỡng khách cũ chạy bao lâu?", a: "Tự động tới 10 năm. Kỷ niệm, sinh nhật, cập nhật thị trường khu phố hàng quý và check-in theo mùa — tất cả gửi bằng giọng văn của bạn. Hầu hết môi giới thấy giới thiệu và quay lại tăng trong 18 tháng đầu sau khi bật nuôi dưỡng." },
      ]}
      outro={{
        title: "Hãy là môi giới",
        italic: "họ nhớ đến.",
        description: "Mỗi lần chạm là một listing tương lai. Mỗi đánh giá là một khách hàng tương lai. Mỗi lần nuôi dưỡng là một lời giới thiệu đang chờ.",
      }}
      afterFaq={<REUseCases />}
    />
  );
}

function REUseCases() {
  const items = [
    { i: Home, t: "Môi giới listing", d: "Chạm-để-chia-sẻ listing, thu sự quan tâm của khách, đặt lịch xem nhà không cần gọi qua lại.", to: "/vi/platform/bookings" },
    { i: Users, t: "Môi giới bên mua", d: "Đánh giá khách tại chỗ, gửi công bố, ký ủy quyền mua ngay ở bãi đỗ xe.", to: "/vi/platform/contracts" },
    { i: Key, t: "Quản lý bất động sản", d: "Mã QR tại mỗi căn, yêu cầu bảo trì được định tuyến tự động.", to: "/vi/platform/networking/qr-code" },
    { i: MapPin, t: "Sàn giao dịch & đội ngũ", d: "Thẻ có thương hiệu cho toàn bộ môi giới kèm báo cáo trung tâm & SSO.", to: "/vi/solutions/enterprise" },
  ];
  return (
    <section className="bg-background">
      <div className="mx-auto max-w-7xl px-4 py-20 md:px-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Tình huống bất động sản</p>
        <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
          Mọi vai trò trong sàn, <em className="italic">đều được phục vụ.</em>
        </h2>
        <div className="mt-10 grid gap-4 md:grid-cols-2">
          {items.map((i) => {
            const Icon = i.i;
            return (
              <Link key={i.t} to={i.to} className="group rounded-2xl bg-muted p-6 transition hover:bg-muted/70">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-soft">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="mt-4 text-lg font-bold">{i.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{i.d}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-bold underline-offset-4 group-hover:underline">
                  Khám phá <ArrowRight className="h-4 w-4" />
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}